import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Container,
  Alert,
  CircularProgress
} from '@mui/material';
import { Business } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Company } from '../../types/auth';

interface CompanySelectorProps {
  onCompanySelect: (company: Company) => void;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({ onCompanySelect }) => {
  const navigate = useNavigate();
  const { user, selectCompany } = useAuth();
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user has previously selected company
    const savedCompany = localStorage.getItem('selectedCompany');
    if (savedCompany && user?.companies) {
      try {
        const company = JSON.parse(savedCompany);
        // Verify company is still accessible
        if (user.companies.find(c => c.id === company.id)) {
          setSelectedCompany(company);
        }
      } catch (error) {
        console.error('Error parsing saved company:', error);
        localStorage.removeItem('selectedCompany');
      }
    }
  }, [user?.companies]);

  const handleCompanyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const companyId = parseInt(event.target.value);
    const company = user?.companies.find(c => c.id === companyId);
    if (company) {
      setSelectedCompany(company);
    }
  };

  const handleContinue = async () => {
    if (selectedCompany) {
      setLoading(true);
      try {
        selectCompany(selectedCompany);
        onCompanySelect(selectedCompany);
      } catch (error) {
        console.error('Error selecting company:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  if (!user?.companies || user.companies.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Business color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No Companies Available
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You don't have access to any companies. Please contact your administrator.
            </Typography>
            <Alert severity="warning" sx={{ mb: 3 }}>
              Contact your system administrator to gain access to companies.
            </Alert>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={() => navigate('/auth/logout')}
              sx={{ minWidth: 120 }}
            >
              Logout
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Business color="primary" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Select Company
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You have access to multiple companies. Please select one to continue:
            </Typography>
          </Box>

          <RadioGroup
            value={selectedCompany?.id || ''}
            onChange={handleCompanyChange}
            sx={{ mb: 3 }}
          >
            {user.companies.map((company) => (
              <FormControlLabel
                key={company.id}
                value={company.id}
                control={<Radio />}
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="subtitle1">
                      {company.name}
                    </Typography>
                  </Box>
                }
                sx={{
                  border: '1px solid',
                  borderColor: selectedCompany?.id === company.id ? 'primary.main' : 'grey.300',
                  borderRadius: 1,
                  mb: 1,
                  p: 2,
                  m: 0,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              />
            ))}
          </RadioGroup>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleContinue}
              disabled={!selectedCompany || loading}
              startIcon={loading ? <CircularProgress size={20} /> : undefined}
              sx={{ minWidth: 120 }}
            >
              {loading ? 'Loading...' : 'Continue'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CompanySelector;